import { useQuery } from '@tanstack/react-query';

import { useBrandKits } from '@/hooks/useBrandKits';
import { supabase } from '@/integrations/supabase/client';
import type { EditorialPillar, EditorialTopic } from '@/lib/reach/editorial-chain';
import { readPillars } from '@/lib/reach/editorial-pillars';

/**
 * The editorial map's one read.
 *
 * TWO SOURCES, ONE SHAPE. The taxonomy (pillars and their branches) lives in
 * `brand_kits.brand_guidelines.pillars[]`; the topics live in `project_tasks`
 * and reference it by `pillar_id` / `branch_id`. Nothing stores a pillar's
 * NAME on a topic, so a rename cannot orphan anything — names resolve here, at
 * read time, which is the whole reason ids had to become stable first (see
 * `src/lib/plans/reconcile-pillars.ts` for the defect that forced it).
 *
 * 🔴 COUNTS ARE NOT AGGREGATED IN THE CLIENT. PostgREST silently caps a
 * `.select()` at ~1000 rows, and this repo has already shipped a card that
 * described 0.85% of a table as the whole thing. Every count below is derived
 * from rows this query actually fetched for ONE workspace — a bounded set of
 * planned topics, never a table scan — and the row cap is set explicitly so a
 * workspace that ever exceeds it fails loudly rather than under-reporting.
 */

const TOPIC_ROW_CAP = 500;

export interface EditorialTopicRow extends EditorialTopic {
  /**
   * `project_tasks.project_id`. Carried so the Write action can hand the agent
   * the topic's identity structurally rather than as prose — see
   * `context-types.ts`'s `editorialTopic`, which is the other half of the join
   * that makes `hasArticle` reachable.
   */
  projectId: string;
  /**
   * `project_tasks.artifact_id` — an `ai_messages.id` when `hasArticle`, which
   * is what `validate_project_artifact_ref` requires for a document. This is
   * the only handle on the written article: the markdown lives inside that
   * message's `parts`, so without it a row could say "written" and offer the
   * reader no way to read it. See `useOpenEditorialArticle`.
   */
  artifactId: string | null;
  pillarId: string | null;
  /** `project_tasks.rationale` — the sheet's "Primary audience" column. */
  audience: string | null;
  /** `project_tasks.summary` — the sheet's "Reference / inspiration" column. */
  reference: string | null;
}

export interface EditorialMap {
  pillars: EditorialPillar[];
  /** Every topic, flat, for the table view. */
  allTopics: EditorialTopicRow[];
  topicsByPillar: Record<string, EditorialTopic[]>;
  /** Topics carrying a missing/retired pillar id, retained as addressable history. */
  orphanedTopics: EditorialTopicRow[];
  /** True when the workspace has more topics than this read will show. */
  truncated: boolean;
}

export function useEditorialMap({ enabled = true }: { enabled?: boolean } = {}) {
  const { activeKit } = useBrandKits();
  const workspaceId = activeKit?.id ?? null;

  return useQuery<EditorialMap>({
    queryKey: ['editorial-map', workspaceId],
    // `enabled: false` lets a non-Editorial surface (the Publishing calendar) skip the reads entirely
    // for a workspace that cannot open /reach/editorial.
    enabled: enabled && Boolean(workspaceId),
    queryFn: async () => {
      const { data: kit, error: kitError } = await supabase
        .from('brand_kits')
        .select('brand_guidelines')
        .eq('id', workspaceId as string)
        .maybeSingle();
      if (kitError) throw new Error(kitError.message);

      const pillars = readPillars(kit?.brand_guidelines);

      const { data: projects, error: projectError } = await supabase
        .from('projects')
        .select('id')
        .eq('brand_kit_id', workspaceId as string)
        .is('archived_at', null);
      if (projectError) throw new Error(projectError.message);

      const projectIds = (projects ?? []).map((p) => p.id);
      if (!projectIds.length) {
        return { pillars, allTopics: [], topicsByPillar: {}, orphanedTopics: [], truncated: false };
      }

      const { data: tasks, error: taskError } = await supabase
        .from('project_tasks')
        .select(
          'id, project_id, topic, pillar_id, branch_id, status, artifact_kind, artifact_id, rationale, summary',
        )
        .in('project_id', projectIds)
        .limit(TOPIC_ROW_CAP + 1);
      if (taskError) throw new Error(taskError.message);

      const rows = tasks ?? [];
      const truncated = rows.length > TOPIC_ROW_CAP;

      const known = new Set(pillars.map((p) => p.id));
      const topicsByPillar: Record<string, EditorialTopic[]> = {};
      const orphanedTopics: EditorialTopicRow[] = [];
      const allTopics: EditorialTopicRow[] = [];

      for (const row of rows.slice(0, TOPIC_ROW_CAP)) {
        const topic: EditorialTopic = {
          id: row.id,
          topic: row.topic ?? 'Untitled topic',
          branchId: row.branch_id ?? null,
          status: row.status ?? null,
          hasArticle: row.artifact_kind === 'document',
        };
        const pillarId = row.pillar_id as string | null;
        const fullTopic: EditorialTopicRow = {
          ...topic,
          projectId: row.project_id,
          artifactId: row.artifact_id ?? null,
          pillarId,
          audience: row.rationale ?? null,
          reference: row.summary ?? null,
        };
        if (pillarId && known.has(pillarId)) {
          // Only topics whose pillar RESOLVES belong in the map. A topic
          // carrying a pillar id minted by an older plan generator (before
          // `reconcile-pillars.ts` made ids workspace-stable) is not "unfiled"
          // — it is filed against a taxonomy that no longer exists, and
          // listing it beside real ones as "Not filed" reads as a data-entry
          // gap rather than the migration artefact it is.
          allTopics.push(fullTopic);
          (topicsByPillar[pillarId] ??= []).push(topic);
        } else {
          // A pillar deleted from the taxonomy leaves its topics behind. They
          // are reported, never dropped — there is no FK into a jsonb array
          // and there should not be one.
          orphanedTopics.push(fullTopic);
        }
      }

      return { pillars, allTopics, topicsByPillar, orphanedTopics, truncated };
    },
  });
}
